import * as React from 'react';
import { Icon } from './ui/Icon';
import { SocialIcon } from './ui/SocialIcon';
import type { SocialIconName } from './ui/SocialIcon';
import { Wordmark } from './ui/Wordmark';
import { FooterSubscribe } from './FooterSubscribe';
import { useContent, text, link } from '../hooks/useContent';

type FooterProps = {
  /**
   * SPA navigation callback for the legal links. r3f's <Canvas> mounts a
   * separate React reconciler, so react-router's NavigationContext doesn't
   * reach Footer when it's rendered inside <Scroll html>. App.js passes
   * `navigate` from outside the Canvas; falls back to a full-page load if
   * the prop is absent.
   */
  onLegalNavigate?: (path: string) => void;
};

export function Footer({ onLegalNavigate }: FooterProps = {}) {
  const { data: content } = useContent();

  // Footer is rendered both outside and inside the Canvas <Scroll html>, where
  // react-router's NavigationContext is absent — so quick links are plain <a>
  // full-page loads, not router <Link>s. A raw absolute href like
  // "/cards/guidance" would drop the app basename if one is set, and 404. On
  // the apex domain (dennisvalchez.com) PUBLIC_URL is empty, so this is a
  // no-op there; it only matters for worktree dev servers that set a
  // non-empty PUBLIC_URL. Internal links are prefixed with it at build time
  // (same basename BrowserRouter uses; works without router context).
  const base = process.env.PUBLIC_URL || '';
  const internalHref = (href: string) => `${base}${href}`;

  const quickLinks: { label: string; href: string; external?: boolean }[] = [
    { label: text(content, 'footer.link.services'), href: '/#services' },
    { label: text(content, 'footer.link.decks'), href: '/#decks' },
    { label: text(content, 'footer.link.mediumshipCards'), href: '/cards/mediumship' },
    { label: text(content, 'footer.link.guidanceCards'), href: '/cards/guidance' },
    { label: text(content, 'footer.link.dailyCard'), href: '/card-of-the-day' },
    { label: text(content, 'nav.reading'), href: '/reading' },
    { label: text(content, 'nav.radio'), href: '/spirit-box' },
    { label: text(content, 'nav.reviews'), href: '/reviews' },
    { label: text(content, 'footer.link.about'), href: '/#about' },
    { label: text(content, 'footer.link.verify'), href: '/verify' },
    { label: text(content, 'footer.link.tip'), href: link(content, 'tip.stripe'), external: true },
  ];

  const allSocialLinks: { label: SocialIconName; href: string }[] = [
    { label: 'TikTok', href: link(content, 'social.tiktok') },
    { label: 'Instagram', href: link(content, 'social.instagram') },
    { label: 'Facebook', href: link(content, 'social.facebook') },
    { label: 'YouTube', href: link(content, 'social.youtube') },
    { label: 'X', href: link(content, 'social.x') },
    { label: 'Discord', href: link(content, 'social.discord') },
    { label: 'VKVideo', href: link(content, 'social.vkvideo') },
    { label: 'Telegram', href: link(content, 'social.telegram') },
    { label: 'Email', href: link(content, 'social.email') },
  ];
  // A social link with no URL configured in the CMS is hidden, not rendered empty.
  const socialLinks = allSocialLinks.filter((l) => l.href);

  const handleLegalClick = React.useCallback(
    (path: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onLegalNavigate) return;
      // Let cmd/ctrl/middle-click / target-blank still open in a new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      onLegalNavigate(path);
    },
    [onLegalNavigate],
  );

  return (
    <footer className="bg-primary text-white">
      <div className="t-container pt-16 pb-8">
        <div className="grid grid-cols-1 gap-12 md:[grid-template-columns:1.3fr_1fr_1.5fr]">
          {/* Brand wordmark + social icons beneath it */}
          <div>
            <Wordmark
              size="lg"
              inverse
              align="center-md-left"
              taglineTitle={text(content, 'brand.tagline') || undefined}
            />
            <ul className="mt-6 flex items-center justify-center gap-[18px] text-white/85 md:justify-start">
              {socialLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    {...(l.href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                    className="inline-flex text-[22px] transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded"
                    aria-label={l.label}
                  >
                    <span className="sr-only">{l.label}</span>
                    <SocialIcon name={l.label} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="t-label-heavy m-0">{text(content, 'footer.quicklinks.heading')}</h4>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.external ? l.href : internalHref(l.href)}
                    {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="text-[13px] text-white/85 transition hover:text-white"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter / subscribe form */}
          <div>
            <FooterSubscribe onLegalNavigate={onLegalNavigate} />
          </div>
        </div>

        {/* Single legal row — hairline divider, disclaimer left, links right */}
        <div className="t-disclaimer mt-12 pt-6 border-t border-white/20 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-[color:var(--white-70)]">
          <div className="text-[10px] normal-case tracking-normal leading-relaxed whitespace-pre-line md:max-w-2xl">
            {text(content, 'footer.disclaimer')}
          </div>
          {/* Wraps: the four nowrap labels are ~448px wide in RU — a single
              row would exceed the mobile viewport and blow up the layout. */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 md:flex-nowrap">
            <a
              href="/terms"
              onClick={handleLegalClick('/terms')}
              className="whitespace-nowrap transition hover:text-white"
            >
              {text(content, 'footer.legal.terms')}
            </a>
            <a
              href="/privacy"
              onClick={handleLegalClick('/privacy')}
              className="whitespace-nowrap transition hover:text-white"
            >
              {text(content, 'footer.legal.privacy')}
            </a>
            <a
              href="/return-policy"
              onClick={handleLegalClick('/return-policy')}
              className="whitespace-nowrap transition hover:text-white"
            >
              {text(content, 'footer.legal.returnPolicy')}
            </a>
            <a
              href="/warranty-policy"
              onClick={handleLegalClick('/warranty-policy')}
              className="whitespace-nowrap transition hover:text-white"
            >
              {text(content, 'footer.legal.warrantyPolicy')}
            </a>
          </div>
        </div>

        {/* Author credit — hidden until the CMS keys arrive (stale cache /
            backend not yet deployed). */}
        {text(content, 'footer.credit.madeWith') && (
          <div className="mt-6 flex items-center justify-center gap-1 text-[11px] text-[color:var(--white-70)]">
            <span>{text(content, 'footer.credit.madeWith')}</span>
            <Icon name="heart" />
            <span>{text(content, 'footer.credit.by')}</span>
            <a
              href={link(content, 'footer.credit.site')}
              target="_blank"
              rel="noopener noreferrer"
              className="transition hover:text-white"
            >
              {text(content, 'footer.credit.siteName')}
            </a>
          </div>
        )}
      </div>
    </footer>
  );
}
